import { type ReactElement } from "react";

import { Hotkey } from "@btnet/core-components/Hotkey";
import { HotkeyCommands } from "@btnet/core-components/Hotkey/hotkey.utility";

export interface ISearchResultKeyboardNavProps {
    /** The search field and its dropdown. */
    children: ReactElement;

    isOpen: boolean;

    resultCount: number;

    onNavigateUp: () => void;

    onNavigateDown: () => void;

    /** Fired on Enter while a result is highlighted. */
    onSelectActive: () => void;
}

/**
 * Arrow-key and Enter handling for a search field with a results dropdown. Uses `Hotkey` rather
 * than a keydown handler so the keys are only claimed while results are showing — otherwise arrow
 * keys get swallowed everywhere, which breaks rich text editors.
 */
export function SearchResultKeyboardNav({
    children,
    isOpen,
    resultCount,
    onNavigateUp,
    onNavigateDown,
    onSelectActive,
}: Readonly<ISearchResultKeyboardNavProps>) {
    // Enter is claimed whenever the dropdown shows; the arrows only when there's something to move
    // through.
    const navigationDisabled = !isOpen || resultCount === 0;

    return (
        <Hotkey
            {...HotkeyCommands["enter"]}
            onCommand={onSelectActive}
            disabled={!isOpen}
            preventDefaultWhenDisabled={false}
        >
            <Hotkey
                {...HotkeyCommands["navDown"]}
                onCommand={onNavigateDown}
                disabled={navigationDisabled}
                preventDefaultWhenDisabled={false}
            >
                <Hotkey
                    {...HotkeyCommands["navUp"]}
                    onCommand={onNavigateUp}
                    disabled={navigationDisabled}
                    preventDefaultWhenDisabled={false}
                >
                    {children}
                </Hotkey>
            </Hotkey>
        </Hotkey>
    );
}
